// Kassen-Hooks: Bar-Annahmen, Übergabe, Receipts.

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";

export type PaymentStatus = "pending" | "authorized" | "paid" | "failed" | "refunded" | "cash_pending_handover" | "cash_handed_over";

export interface Payment {
  id: string;
  order_id: string;
  method: string;
  amount_cents: number;
  status: PaymentStatus;
  external_ref: string | null;
  collected_by: string | null;
  paid_at: string | null;
  created_at: string;
}

export interface CashHandover {
  id: string;
  shift_id: string;
  driver_id: string;
  soll_cents: number;
  ist_cents: number;
  diff_cents: number;
  witness_id: string | null;
  note: string | null;
  handed_over_at: string;
}

export interface Receipt {
  id: string;
  order_id: string;
  receipt_number: string;
  pdf_url: string | null;
  printed_at: string | null;
  created_at: string;
}

// ---------- Bar-Annahmen ----------

/** Alle offenen Bar-Annahmen (cash_pending_handover) eines Tages */
export function useCashPendingForToday(driverId?: string) {
  return useQuery({
    queryKey: ["cash-today", driverId ?? "all"],
    queryFn: async () => {
      let q = supabase
        .from("payments")
        .select("*, order:orders(order_number, customer:customers(display_name))")
        .eq("method", "cash")
        .eq("status", "cash_pending_handover")
        .order("paid_at", { ascending: false });
      if (driverId) q = q.eq("collected_by", driverId);
      const { data, error } = await q;
      if (error) throw error;
      return (data ?? []) as Array<
        Payment & { order: { order_number: string | null; customer: { display_name: string } | null } | null }
      >;
    },
  });
}

/** Soll-Summe heute je Fahrer (für Dashboard) */
export function useCashSummaryByDriver(date: string) {
  return useQuery({
    queryKey: ["cash-summary", date],
    queryFn: async () => {
      const dayStart = `${date}T00:00:00Z`;
      const dayEnd = `${date}T23:59:59Z`;
      const { data, error } = await supabase
        .from("payments")
        .select("collected_by, amount_cents, status, paid_at, collector:profiles!payments_collected_by_fkey(full_name)")
        .eq("method", "cash")
        .gte("paid_at", dayStart)
        .lte("paid_at", dayEnd);
      if (error) throw error;
      // Gruppieren je collected_by
      const map = new Map<string, { name: string; soll: number; pending: number; uebergeben: number }>();
      for (const p of data ?? []) {
        const key = p.collected_by ?? "?";
        const collector = Array.isArray(p.collector) ? p.collector[0] : p.collector;
        const name = collector?.full_name ?? key.slice(0, 8);
        const e = map.get(key) ?? { name, soll: 0, pending: 0, uebergeben: 0 };
        e.soll += p.amount_cents;
        if (p.status === "cash_pending_handover") e.pending += p.amount_cents;
        if (p.status === "cash_handed_over") e.uebergeben += p.amount_cents;
        map.set(key, e);
      }
      return Array.from(map.entries()).map(([id, v]) => ({ driver_id: id, ...v }));
    },
  });
}

// ---------- Kasse abgeben ----------

/**
 * Wie viel Bargeld liegt aus DIESER Tour noch beim Fahrer?
 *
 * Die Soll-Summe wird beim Abgeben serverseitig noch einmal gerechnet (siehe
 * kasse_abgeben). Diese Abfrage ist nur die Anzeige — der Fahrer soll sehen,
 * womit er es zu tun hat, bevor er zählt.
 */
export function useOffeneKasse(tourId: string | undefined) {
  return useQuery({
    queryKey: ["kasse-tour", tourId],
    enabled: !!tourId,
    queryFn: async () => {
      // Über die Stops der Tour: Eine Zahlung hängt an einer Bestellung, und die
      // Bestellung hängt über den Stop an der Tour.
      const { data: stops, error: sErr } = await supabase
        .from("delivery_stops")
        .select("order_id")
        .eq("tour_id", tourId!);
      if (sErr) throw sErr;

      const orderIds = (stops ?? []).map((s) => s.order_id).filter(Boolean);
      if (!orderIds.length) return { summe: 0, anzahl: 0 };

      const { data, error } = await supabase
        .from("payments")
        .select("amount_cents")
        .in("order_id", orderIds)
        .eq("method", "cash")
        .eq("status", "cash_pending_handover");
      if (error) throw error;

      return {
        summe: (data ?? []).reduce((s, p) => s + (p.amount_cents ?? 0), 0),
        anzahl: (data ?? []).length,
      };
    },
  });
}

/** Wurde diese Tour schon abgegeben? Dann darf es kein zweites Mal passieren. */
export function useKassenAbgabe(tourId: string | undefined) {
  return useQuery({
    queryKey: ["kassenabgabe", tourId],
    enabled: !!tourId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("cash_handovers")
        .select("id, soll_cents, ist_cents, diff_cents, note, handed_over_at, witness:profiles!cash_handovers_witness_id_fkey(full_name)")
        .eq("tour_id", tourId!)
        .maybeSingle();
      if (error) throw error;
      return data as {
        id: string;
        soll_cents: number;
        ist_cents: number;
        diff_cents: number;
        note: string | null;
        handed_over_at: string;
        witness: { full_name: string | null } | null;
      } | null;
    },
  });
}

/**
 * Kasse abgeben — Beleg schreiben UND die Zahlungen abhaken, in EINEM Schritt.
 *
 * Läuft über die Datenbankfunktion `kasse_abgeben`, nicht über zwei Aufrufe:
 * Bricht es dazwischen ab, stünde sonst ein Beleg über 1.800 € da, während das
 * Geld weiter als "beim Fahrer" gilt. Und die Soll-Summe rechnet der Server
 * selbst — ein Client, der sie mitschickt, könnte sich verrechnen.
 */
export function useKasseAbgeben(tourId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      istCents,
      witnessId,
      note,
    }: {
      istCents: number;
      witnessId?: string | null;
      note?: string | null;
    }) => {
      const { data, error } = await supabase.rpc("kasse_abgeben", {
        p_tour_id: tourId!,
        p_ist_cents: istCents,
        p_witness_id: witnessId || null,
        p_note: note || null,
      });
      if (error) throw error;
      const zeile = Array.isArray(data) ? data[0] : data;
      return zeile as {
        handover_id: string;
        soll_cents: number;
        ist_cents: number;
        diff_cents: number;
        zahlungen: number;
      };
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["kasse-tour", tourId] });
      qc.invalidateQueries({ queryKey: ["kassenabgabe", tourId] });
      qc.invalidateQueries({ queryKey: ["kassenlage"] });
      qc.invalidateQueries({ queryKey: ["cash-today"] });
    },
  });
}

// ---------- Alt: Übergabe über die Schicht ----------

export function useCashHandover() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ shiftId, driverId, sollCents, istCents, witnessId, note }: {
      shiftId: string;
      driverId: string;
      sollCents: number;
      istCents: number;
      witnessId?: string;
      note?: string;
    }) => {
      const { data: handover, error } = await supabase
        .from("cash_handovers")
        .insert({
          shift_id: shiftId,
          driver_id: driverId,
          soll_cents: sollCents,
          ist_cents: istCents,
          witness_id: witnessId ?? null,
          note: note ?? null,
        })
        .select()
        .single();
      if (error) throw error;
      // Alle pending Payments dieses Fahrers heute auf 'cash_handed_over' setzen
      await supabase
        .from("payments")
        .update({ status: "cash_handed_over", external_ref: handover.id })
        .eq("collected_by", driverId)
        .eq("status", "cash_pending_handover");
      return handover as CashHandover;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["cash-today"] });
      qc.invalidateQueries({ queryKey: ["cash-summary"] });
    },
  });
}

export function useHandovers(date: string) {
  return useQuery({
    queryKey: ["handovers", date],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("cash_handovers")
        .select("*, driver:profiles!cash_handovers_driver_id_fkey(full_name), witness:profiles!cash_handovers_witness_id_fkey(full_name)")
        .gte("handed_over_at", `${date}T00:00:00Z`)
        .lte("handed_over_at", `${date}T23:59:59Z`)
        .order("handed_over_at", { ascending: false });
      if (error) throw error;
      return (data ?? []) as Array<CashHandover & {
        driver: { full_name: string | null } | null;
        witness: { full_name: string | null } | null;
      }>;
    },
  });
}
